/**
 * Unified Auth Provider
 *
 * Provides authentication that works seamlessly for both web (Clerk) and native (Flutter bridge).
 * - On web: wraps ClerkProvider and uses Clerk's auth state
 * - On native: uses the native bridge for auth state and token management
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  ClerkProvider,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from "@clerk/clerk-react";
import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import { setAuthTokenGetter } from "../api";
import { useTheme } from "../useTheme";

// User object shape that works for both platforms
export interface AuthUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  imageUrl: string | null;
}

export interface AuthContextValue {
  /** Whether the auth state has been loaded */
  isLoaded: boolean;

  /** Whether the user is signed in */
  isSignedIn: boolean;

  /** The current user ID */
  userId: string | null;

  /** The current user object (if signed in) */
  user: AuthUser | null;

  /** Sign out the current user */
  signOut: () => Promise<void>;

  /** Get the current auth token */
  getToken: () => Promise<string | null>;

  /** Whether running in native context */
  isNative: boolean;

  /** Show native account dialog (only works on native) */
  showAccountDialog: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Native Auth Provider (for Flutter app)
// ============================================================================

interface NativeAuthState {
  isLoaded: boolean;
  userId: string | null;
  token: string | null;
  user: AuthUser | null;
}

function NativeAuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<NativeAuthState>({
    isLoaded: false,
    userId: null,
    token: null,
    user: null,
  });

  // Fetch initial auth state from native
  useEffect(() => {
    async function initAuth() {
      try {
        // Try to get existing token from native storage
        const token = await bridge.request<string | null>(
          BridgeChannels.AUTH,
          BridgeActions.GET_TOKEN
        );

        if (token) {
          // We have a token, try to get user info
          // For now, we'll decode the JWT to get the userId or store it separately
          // The native app should provide this info
          const userId = await bridge.request<string | null>(
            BridgeChannels.AUTH,
            "getUserId"
          );

          const user = await bridge.request<AuthUser | null>(
            BridgeChannels.AUTH,
            "getUser"
          );

          setAuthState({
            isLoaded: true,
            userId,
            token,
            user,
          });
        } else {
          setAuthState({
            isLoaded: true,
            userId: null,
            token: null,
            user: null,
          });
        }
      } catch (e) {
        console.error("[NativeAuthProvider] Init error:", e);
        setAuthState({
          isLoaded: true,
          userId: null,
          token: null,
          user: null,
        });
      }
    }

    initAuth();
  }, []);

  // Listen for auth state changes from native
  useEffect(() => {
    const unsubscribe = bridge.on(
      BridgeChannels.AUTH,
      BridgeActions.STATE_CHANGED,
      (payload: { userId: string | null; token: string | null; user: AuthUser | null }) => {
        setAuthState({
          isLoaded: true,
          userId: payload.userId,
          token: payload.token,
          user: payload.user,
        });
      }
    );

    return unsubscribe;
  }, []);

  // Set up auth token getter for API calls
  const getToken = useCallback(async () => {
    try {
      return await bridge.request<string | null>(
        BridgeChannels.AUTH,
        BridgeActions.GET_TOKEN
      );
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    setAuthTokenGetter(getToken);
  }, [getToken]);

  const signOut = useCallback(async () => {
    try {
      await bridge.request(BridgeChannels.AUTH, BridgeActions.SIGN_OUT);
      setAuthState({
        isLoaded: true,
        userId: null,
        token: null,
        user: null,
      });
    } catch (e) {
      console.error("[NativeAuthProvider] Sign out error:", e);
    }
  }, []);

  const showAccountDialog = useCallback(async () => {
    try {
      const result = await bridge.request<{ action: string }>(
        BridgeChannels.AUTH,
        BridgeActions.SHOW_ACCOUNT_DIALOG,
        undefined,
        60000 * 5 // 5 minutes
      );
      // If user signed out from dialog, update local state
      if (result.action === 'signOut') {
        setAuthState({
          isLoaded: true,
          userId: null,
          token: null,
          user: null,
        });
      }
    } catch (e) {
      console.error("[NativeAuthProvider] Show account dialog error:", e);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoaded: authState.isLoaded,
      isSignedIn: !!authState.token,
      userId: authState.userId,
      user: authState.user,
      signOut,
      getToken,
      isNative: true,
      showAccountDialog,
    }),
    [authState, signOut, getToken, showAccountDialog]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// Clerk Auth Provider (for web)
// ============================================================================

function ClerkAuthInner({ children }: { children: React.ReactNode }) {
  const { getToken, signOut, userId, isLoaded: isAuthLoaded } = useClerkAuth();
  const { user, isLoaded: isUserLoaded } = useClerkUser();

  // Set up auth token getter for API calls
  useEffect(() => {
    setAuthTokenGetter(getToken);
  }, [getToken]);

  const handleSignOut = useCallback(async () => {
    await signOut();
  }, [signOut]);

  // No-op on web - Clerk's UserButton handles this
  const showAccountDialog = useCallback(async () => {
    // Web doesn't use native account dialog
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoaded: isAuthLoaded && isUserLoaded,
      isSignedIn: !!userId,
      userId: userId ?? null,
      user: user ? {
        id: user.id,
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        email: user.primaryEmailAddress?.emailAddress ?? null,
        imageUrl: user.imageUrl ?? null,
      } : null,
      signOut: handleSignOut,
      getToken,
      isNative: false,
      showAccountDialog,
    }),
    [isAuthLoaded, isUserLoaded, userId, user, handleSignOut, getToken, showAccountDialog]
  );
  useEffect(() => {
    if (user != null) {
      const consentData = (window as any).getCkyConsent?.();
      const consentID = consentData?.consentID;
      if (consentID) {
        user.update({unsafeMetadata: {'cookie_consent_id': consentID}});
      }
    }
  }, [user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface WebAuthProviderProps {
  children: React.ReactNode;
  clerkPublishableKey: string;
}

function WebAuthProvider({
  children,
  clerkPublishableKey,
}: WebAuthProviderProps) {
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';

  const clerkAppearance = {
    baseTheme: isDark ? undefined : undefined,
    variables: {
      colorPrimary: '#cf029c',
      colorBackground: isDark ? '#262626' : '#ffffff',
      colorText: isDark ? '#f5f5f5' : '#1a1a1a',
      colorTextSecondary: isDark ? '#a3a3a3' : '#666666',
      colorInputBackground: isDark ? '#404040' : '#f5f5f5',
      colorInputText: isDark ? '#f5f5f5' : '#1a1a1a',
      borderRadius: '0.75rem',
    },
    elements: {
      card: isDark ? 'bg-neutral-800 border-neutral-700' : '',
      headerTitle: isDark ? 'text-neutral-100' : '',
      headerSubtitle: isDark ? 'text-neutral-400' : '',
      socialButtonsBlockButton: isDark ? 'bg-neutral-700 border-neutral-600 text-neutral-100 hover:bg-neutral-600' : '',
      formFieldLabel: isDark ? 'text-neutral-200' : '',
      formFieldInput: isDark ? 'bg-neutral-700 border-neutral-600 text-neutral-100' : '',
      footerActionLink: isDark ? 'text-primary-400 hover:text-primary-300' : '',
      identityPreview: isDark ? 'bg-neutral-700 border-neutral-600' : '',
      identityPreviewText: isDark ? 'text-neutral-100' : '',
      identityPreviewEditButton: isDark ? 'text-primary-400' : '',
      formButtonPrimary: 'bg-primary-600 hover:bg-primary-500',
      userButtonPopoverCard: isDark ? 'bg-neutral-800 border-neutral-700' : '',
      userButtonPopoverActionButton: isDark ? 'text-neutral-200 hover:bg-neutral-700' : '',
      userButtonPopoverActionButtonText: isDark ? 'text-neutral-200' : '',
      userButtonPopoverActionButtonIcon: isDark ? 'text-neutral-400' : '',
      userButtonPopoverFooter: isDark ? 'border-neutral-700' : '',
      userPreviewMainIdentifier: isDark ? 'text-neutral-100' : '',
      userPreviewSecondaryIdentifier: isDark ? 'text-neutral-400' : '',
    },
  };

  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      afterSignInUrl="/app"
      afterSignUpUrl="/app"
      appearance={clerkAppearance}
    >
      <ClerkAuthInner>{children}</ClerkAuthInner>
    </ClerkProvider>
  );
}

// ============================================================================
// Main Auth Provider (auto-selects based on platform)
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({
  children,
}: AuthProviderProps) {
  const isNative = bridge.isNative;

  if (isNative) {
    return <NativeAuthProvider>{children}</NativeAuthProvider>;
  }

  const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  if (!clerkPubKey) {
    throw new Error("Missing Clerk Publishable Key");
  }
  
  return (
    <WebAuthProvider clerkPublishableKey={clerkPubKey}>
      {children}
    </WebAuthProvider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access auth context
 * Use this instead of Clerk's useAuth/useUser for platform-agnostic auth
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Convenience hook to check if user is signed in
 */
export function useIsSignedIn(): boolean {
  return useAuth().isSignedIn;
}

/**
 * Convenience hook to get current user ID
 */
export function useUserId(): string | null {
  return useAuth().userId;
}

/**
 * Convenience hook to get current user
 */
export function useUser(): AuthUser | null {
  return useAuth().user;
}

